import React from "react"
import * as DateFns from "date-fns"
import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/solid"

import { Idea, WebSource } from "../../../../../model/types"
import Model from "../../../../../model/model"
import IdeaBox from "../../../../components/idea_box"
import GoalAutocomplete from "../../../../components/goal_autocomplete"

const deschemeUrl = (url: string) => {
  return url.replace(/https?:\/\//, "")
}

export type FilterType = {
  query: string
  goal: null | Idea | "none"
  sortOrder: {
    new: SortOrder
    queued: SortOrder
    read: SortOrder
  }
}

export type SourceListsType = {
  new: WebSource[]
  queued: WebSource[]
  read: WebSource[]
}

export type SourceSortField = "createdAt" | "publicationDate" | "lastReadAt"
export type SortDirection = "asc" | "desc"
export type SortOrder = {
  field: SourceSortField
  direction: SortDirection
}

const sortDate = (source: WebSource, field: SourceSortField): Date | undefined => {
  switch (field) {
    case "createdAt":
      return source.createdAt
    case "publicationDate":
      return source.publicationDate
    case "lastReadAt":
      return source.lastReadAt
  }
}

const compareSources =
  (sortOrder: SortOrder) =>
  (a: WebSource, b: WebSource): number => {
    const aDate = sortDate(a, sortOrder.field)
    const bDate = sortDate(b, sortOrder.field)

    const fallback = DateFns.compareAsc(b.createdAt, a.createdAt)
    if (!aDate && !bDate) return fallback
    if (!aDate) return 1
    if (!bDate) return -1

    const sorted =
      sortOrder.direction == "desc"
        ? DateFns.compareAsc(bDate, aDate)
        : DateFns.compareAsc(aDate, bDate)

    return sorted || fallback
  }

export const filterSources = (model: Model, newFilter: FilterType): SourceListsType => {
  const sources = model
    .getSources()
    .filter((source: WebSource) => {
      return (
        newFilter.query === "" ||
        (source.url &&
          deschemeUrl(source.url).toLowerCase().includes(newFilter.query.toLowerCase())) ||
        (source.name && source.name.toLowerCase().includes(newFilter.query.toLowerCase())) ||
        (source.author && source.author.toLowerCase().includes(newFilter.query.toLowerCase())) ||
        (source.type && source.type.toLowerCase().includes(newFilter.query.toLowerCase()))
      )
    })
    .filter((source: WebSource) => {
      return (
        !newFilter.goal ||
        (newFilter.goal === "none" && source.goalId === null) ||
        (newFilter.goal !== "none" && source.goalId === newFilter.goal.id)
      )
    })

  return {
    new: sources
      .filter((source: WebSource) => source.status == "new")
      .sort(compareSources(newFilter.sortOrder.new)),
    queued: sources
      .filter((source: WebSource) => source.status == "queued")
      .sort(compareSources(newFilter.sortOrder.queued)),
    read: sources
      .filter((source: WebSource) => source.status == "read")
      .sort(compareSources(newFilter.sortOrder.read)),
  }
}

export default function Filter({ filter, setFilter }) {
  return (
    <div id="source-filter" className="flex flex-col">
      <div className="relative z-0 flex flex-1 items-center justify-center">
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
          <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
        </div>
        <input
          type="text"
          placeholder="Search sources"
          name="filter"
          value={filter.query}
          onChange={(e) => {
            const newFilter: FilterType = {
              ...filter,
              query: e.target.value,
            }
            setFilter(newFilter)
          }}
          className="block w-full rounded-md border-0 bg-white dark:bg-gray-800 py-1.5 pl-10 pr-3 text-gray-900 dark:text-gray-300 ring-1 ring-inset ring-gray-300 dark:ring-gray-700 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
        />
        {filter.query != "" && (
          <div
            className="absolute inset-y-0 right-0 flex items-center pr-2 cursor-pointer z-10"
            onClick={() => {
              const newFilter: FilterType = { ...filter, query: "" }
              setFilter(newFilter)
            }}
          >
            <XMarkIcon className="h-5 w-5 text-gray-400" />
          </div>
        )}
      </div>
      <div className="mt-2 flex gap-3">
        {filter.goal && filter.goal !== "none" && (
          <div className="min-w-0 flex justify-start items-center gap-2 ">
            <IdeaBox idea={filter.goal} />
            <div className="text-sm text-gray-600 dark:text-gray-100 overflow-x-hidden whitespace-nowrap text-ellipsis">
              {filter.goal.title}
            </div>
            <div
              className="cursor-pointer text-gray-600 hover:text-gray-400"
              onClick={() => {
                const newFilter: FilterType = { ...filter, goal: null }
                setFilter(newFilter)
              }}
            >
              <XMarkIcon className="w-4 h-4" />
            </div>
          </div>
        )}
        {filter.goal && filter.goal === "none" && (
          <div className="flex justify-between items-center gap-2 min-w-0 overflow-x-hidden">
            <div className="text-sm text-gray-600 dark:text-gray-100 overflow-hidden whitespace-nowrap text-ellipsis">
              No goal
            </div>
            <div
              className="cursor-pointer text-gray-600 hover:text-gray-400"
              onClick={() => {
                const newFilter: FilterType = { ...filter, goal: null }
                setFilter(newFilter)
              }}
            >
              <XMarkIcon className="w-4 h-4" />
            </div>
          </div>
        )}
        {!filter.goal && (
          <div className="flex-grow">
            <GoalAutocomplete
              canSelectNone={true}
              placeholder="Filter by goal"
              select={(result, _filter) => {
                if (result.type == "item") {
                  const goal = result.item
                  const newFilter: FilterType = { ...filter, goal }
                  setFilter(newFilter)
                } else if (result.type == "none") {
                  const newFilter: FilterType = { ...filter, goal: "none" }
                  setFilter(newFilter)
                }
                return true
              }}
              onEscape={() => {}}
              inputStyle="inset"
            />
          </div>
        )}
      </div>
    </div>
  )
}
